import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        success:
          "border-transparent bg-success text-success-foreground hover:bg-success-hover",
        warning:
          "border-transparent bg-warning text-warning-foreground hover:bg-warning/80",
        outline: "text-foreground",
        // Vier tonale Stufen fuer den Quality-Score-Badge (Design-Audit F15).
        //
        // Warum tonal statt deckend: die deckenden Varianten daruber tragen
        // weisse Schrift auf einer Signalfarbe. Gemessen (WCAG 2.x, beide
        // Themes, siehe scoreBadgeContrast.test.ts) faellt das fuer zwei der
        // drei Stufen durch: `warning` liegt bei 2.13:1 (hell) / 1.69:1
        // (dunkel), `destructive` bei 3.78:1 / 2.63:1. Nur `success` besteht
        // (4.76:1 / 10.18:1) -- das Design-Audit hatte hier 3.8:1 vermutet und
        // damit ausgerechnet die einzige Stufe getroffen, die in Ordnung war.
        // Die vier tonalen Paare unten liegen zwischen 4.67:1 und 16.32:1.
        //
        // Kein Hover-Ton: dieser Badge ist keine Schaltflaeche. Die frueher
        // geerbten `hover:bg-*`-Klassen waren die Ursache des B4b-Aufblitzens.
        //
        // Die vierte Stufe "solide" (70-84) kommt bewusst aus den Neutrals und
        // nicht aus einem vierten Buntton: sie ist ein Zwischenzustand ohne
        // Qualitaetsurteil, und ein vierter Signalton wuerde die Ampel
        // entwerten.
        scoreCritical:
          "border-destructive/30 bg-destructive-light text-destructive-ink",
        scoreWarning:
          "border-warning/30 bg-warning-light text-warning-ink",
        scoreSolid:
          "border-border bg-muted text-foreground",
        scoreExcellent:
          "border-success/30 bg-success-light text-success-ink",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
